import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { Design } from '../../api/design';
import {
  type DesignPreset,
  designPresetKeys,
  designPresetsQuery,
} from '../../api/designPreset';
import { getPresetFeatures } from '../utils/designPreset';
import DesignDialog from './DesignDialog';
import DesignWithPresetsDialog from './DesignWithPresetsDialog';

interface DesignDialogSwitchProps {
  onClose: () => void;
  design: Design | null;
  onSuccess: (design: Design, mode: 'create' | 'update') => void;
}

/**
 * Opens DesignWithPresetsDialog for a design that has presets, and
 * DesignDialog otherwise. Mount it per open: the choice is made once, and only
 * changes when the first preset is added from DesignDialog.
 */
export function DesignDialogSwitch({
  onClose,
  design,
  onSuccess,
}: DesignDialogSwitchProps) {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState(design);
  const [layout, setLayout] = useState<'plain' | 'presets' | null>(null);
  const [addedPresetId, setAddedPresetId] = useState<number>();
  const features = getPresetFeatures(current?.vestmentType);
  const presets = useQuery({
    ...designPresetsQuery(current?.id ?? 0),
    enabled: !!current && !!features,
  });

  let resolvedLayout = layout;
  if (resolvedLayout === null) {
    if (!current || !features || presets.isError) {
      resolvedLayout = 'plain';
    } else if (presets.isSuccess) {
      resolvedLayout = presets.data.length > 0 ? 'presets' : 'plain';
    }
    if (resolvedLayout !== null) {
      setLayout(resolvedLayout);
    }
  }

  const addPreset = (preset: DesignPreset) => {
    if (!current) {
      return;
    }
    const queryKey = designPresetKeys.byDesign(current.id);
    queryClient.setQueryData<DesignPreset[]>(queryKey, (cached = []) => [
      ...cached,
      preset,
    ]);
    void queryClient.invalidateQueries({ queryKey });
    setAddedPresetId(preset.id);
    setLayout('presets');
  };

  if (resolvedLayout === 'presets' && current && features) {
    return (
      <DesignWithPresetsDialog
        onClose={onClose}
        design={current}
        features={features}
        onSuccess={(saved) => onSuccess(saved, 'update')}
        initialPresetId={addedPresetId}
      />
    );
  }
  if (resolvedLayout === 'plain') {
    return (
      <DesignDialog
        onClose={onClose}
        design={current}
        onSuccess={(saved, mode) => {
          setCurrent(saved);
          onSuccess(saved, mode);
        }}
        onPresetAdded={addPreset}
      />
    );
  }
  return null;
}
